// Shared card_key parsing + parallel/name lookups.
//
// Extracted from Marketplace.js (Marketplace 2.1 — Sold Feed build, step 2) so
// the marketplace grid and the Sold-listings feed share one implementation
// instead of duplicating it. No behavior change vs. the original Marketplace.js
// copies — same parse rules, same parallel map, same dynamic-import name loader.

import { BASE_PARALLELS, INSERT_PARALLELS, AUTO_PARALLELS, SET_PARALLELS } from '../parallelData';
import { SET_REGISTRY } from '../setRegistry';

// Curated ladders first (first-win in PAR_MAP), then the set-derived supplement
// fills ids that only exist in the per-set checklists (e.g. green-shimmer, gold).
const ALL_PARALLELS = [...BASE_PARALLELS, ...INSERT_PARALLELS, ...AUTO_PARALLELS, ...SET_PARALLELS];

// parallel id -> parallel meta ({ id, name, color, print_run, ... }). First win.
export const PAR_MAP = {};
ALL_PARALLELS.forEach(p => { if (!PAR_MAP[p.id]) PAR_MAP[p.id] = p; });

// Resolve a parallel id to its meta, with a graceful fallback for unknown ids.
export function parallelFor(parallelId) {
  if (!parallelId) return null;
  return PAR_MAP[parallelId] || { name: parallelId, color: '#c8d8f0', print_run: null };
}

// Parse a card_key into its components.
//   shape: `<setId>__<subSetId>__<cardN>[__parallel__<parallelId>][__s__<serial>]`
export function parseCardKey(cardKey) {
  let key = cardKey;
  let serialNum = null;
  if (key.includes('__s__')) { const i = key.lastIndexOf('__s__'); serialNum = key.slice(i + 5); key = key.slice(0, i); }
  const parts = key.split('__');
  let setId = '', subSetId = '', cardN = '', parallelId = null;
  const pIdx = parts.indexOf('parallel');
  if (pIdx !== -1) { parallelId = parts.slice(pIdx + 1).join('__'); key = parts.slice(0, pIdx).join('__'); }
  const kParts = (pIdx !== -1 ? parts.slice(0, pIdx) : parts);
  if (kParts.length >= 3) { cardN = kParts[kParts.length - 1]; subSetId = kParts[kParts.length - 2]; setId = kParts.slice(0, kParts.length - 2).join('__'); }
  return { setId, subSetId, cardN, parallelId, serialNum };
}

// Base card_key (set__sub__cardN, no parallel/serial) — the key used for name lookup.
export function baseCardKey(cardKey) {
  return cardKey.split('__').slice(0, 3).join('__');
}

// Build a card-name lookup for the given setIds by dynamic-importing each set's
// data file. Returns a flat map: `${setId}__${subSetId}__${cardN}` -> name.
// Stateless (no React) so any caller can manage its own state.
export async function loadCardNamesForSets(setIds) {
  const out = {};
  const unique = [...new Set(setIds)].filter(Boolean);
  await Promise.all(unique.map(async (setId) => {
    const setMeta = SET_REGISTRY.find(s => s.id === setId);
    if (!setMeta || !setMeta.dataFile) return;
    try {
      const mod = await import(`../sets/${setMeta.dataFile}.js`);
      const SETS = mod.SETS || [];
      SETS.forEach(s => {
        (s.cards || []).forEach(c => {
          out[`${setId}__${s.id}__${c.n}`] = c.name;
        });
      });
    } catch (e) {
      console.warn(`Could not load card names for set ${setId}:`, e);
    }
  }));
  return out;
}
